import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../components/Modal';
import {
  deleteDilimRewardGroup,
  deleteDilimRewardGroupItem,
  listDilimPointProductSizes,
  listDilimMilestoneSteps,
  listDilimRewardGroupItems,
  listDilimRewardGroups,
  listEligibleDilimRewardSizes,
  type DilimPointProductSizeRecord,
  type DilimRewardGroupItemRecord,
  type EligibleDilimRewardSize,
  updateDilimPointProductSizes,
  upsertDilimMilestoneStep,
  upsertDilimRewardGroup,
  upsertDilimRewardGroupItem,
  type DilimMilestoneStepRecord,
  type DilimRewardGroupRecord,
} from '../services/dilimMilestones';

type StepDraft = {
  rewardGroupId: string;
};

type RewardGroupFormState = {
  id?: string;
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
};

type RewardItemEditState = {
  selectionWeight: string;
  displayOrder: string;
  isActive: boolean;
};

type PageTab = 'milestones' | 'points';
type PointScope = 'all' | 'earning' | 'zero' | 'nonPizzaEarning';

const STEP_NUMBERS = Array.from({ length: 10 }, (_, index) => index + 1);
const EMPTY_GROUP_FORM: RewardGroupFormState = {
  name: '',
  slug: '',
  description: '',
  isActive: true,
};
const EMPTY_MILESTONE_STEPS: DilimMilestoneStepRecord[] = [];
const EMPTY_REWARD_GROUPS: DilimRewardGroupRecord[] = [];
const EMPTY_REWARD_GROUP_ITEMS: DilimRewardGroupItemRecord[] = [];
const EMPTY_REWARD_SIZES: EligibleDilimRewardSize[] = [];
const EMPTY_POINT_SIZES: DilimPointProductSizeRecord[] = [];
const NON_PIZZA_POINT_CATEGORIES = new Set([
  'atistirmalik',
  'atistirmaliklar',
  'yan-urunler',
  'snack',
  'snacks',
  'icecekler',
  'drink',
  'drinks',
  'beverages',
  'sos',
  'soslar',
  'sauce',
]);
const REQUIRED_DILIM_MIGRATIONS = [
  '20260412082429_dilim_milestone_backend.sql',
  '20260412121000_dilim_reward_pool_fallbacks.sql',
];

const normalizeSearch = (value: string) => value.toLocaleLowerCase('tr-TR').trim();
const normalizeCategoryKey = (value: string | null | undefined) => normalizeSearch(value ?? '');
const isLikelyPizzaPointRow = (row: DilimPointProductSizeRecord) =>
  !NON_PIZZA_POINT_CATEGORIES.has(normalizeCategoryKey(row.productCategory));
const normalizePointDraft = (value: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.floor(parsed));
};

const buildStepDraftMap = (steps: DilimMilestoneStepRecord[]): Record<number, StepDraft> =>
  STEP_NUMBERS.reduce<Record<number, StepDraft>>((acc, stepNumber) => {
    const step = steps.find((entry) => entry.stepNumber === stepNumber);
    acc[stepNumber] = {
      rewardGroupId: step?.rewardGroupId ?? '',
    };
    return acc;
  }, {});

const buildGroupFormState = (group?: DilimRewardGroupRecord | null): RewardGroupFormState =>
  group
    ? {
        id: group.id,
        name: group.name,
        slug: group.slug,
        description: group.description ?? '',
        isActive: group.isActive,
      }
    : EMPTY_GROUP_FORM;

type QueryErrorLike = {
  code?: string;
  message?: string;
  details?: string | null;
  hint?: string | null;
};

const asQueryError = (error: unknown): QueryErrorLike | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  return error as QueryErrorLike;
};

const isMissingDilimBackendShapeError = (error: unknown): boolean => {
  const queryError = asQueryError(error);
  const code = queryError?.code ?? '';
  const message = (queryError?.message ?? '').toLocaleLowerCase('tr-TR');

  return (
    code === 'PGRST205' ||
    code === '42P01' ||
    code === '42703' ||
    message.includes('dilim_milestone_steps') ||
    message.includes('dilim_reward_groups') ||
    message.includes('dilim_reward_group_items') ||
    message.includes('product_sizes.dilim_points')
  );
};

const formatLoadErrorMessage = (error: unknown): string => {
  const queryError = asQueryError(error);
  const rawMessage = queryError?.message?.trim();

  if (isMissingDilimBackendShapeError(error)) {
    const migrationList = REQUIRED_DILIM_MIGRATIONS.join(' ve ');
    return `Bu Supabase projesinde Dilim milestone backend şeması eksik görünüyor. Önce ${migrationList} migrationlarını uygulayın. ${rawMessage ? `Ham hata: ${rawMessage}` : ''}`.trim();
  }

  return rawMessage || 'Dilim milestone verileri yüklenemedi.';
};

export default function DilimMilestonesPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<PageTab>('milestones');
  const [stepDrafts, setStepDrafts] = useState<Record<number, StepDraft>>(() => buildStepDraftMap([]));
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [rewardGroupsModalOpen, setRewardGroupsModalOpen] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [groupForm, setGroupForm] = useState<RewardGroupFormState>(EMPTY_GROUP_FORM);
  const [rewardSizeSearch, setRewardSizeSearch] = useState('');
  const [selectedRewardSizeId, setSelectedRewardSizeId] = useState('');
  const [newItemWeight, setNewItemWeight] = useState('1');
  const [newItemDisplayOrder, setNewItemDisplayOrder] = useState('0');
  const [newItemActive, setNewItemActive] = useState(true);
  const [itemDrafts, setItemDrafts] = useState<Record<string, RewardItemEditState>>({});
  const [pointDrafts, setPointDrafts] = useState<Record<string, string>>({});
  const [pointSearch, setPointSearch] = useState('');
  const [pointCategoryFilter, setPointCategoryFilter] = useState('all');
  const [pointScope, setPointScope] = useState<PointScope>('all');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const stepsQuery = useQuery({
    queryKey: ['dilim', 'steps'],
    queryFn: listDilimMilestoneSteps,
  });
  const steps = stepsQuery.data ?? EMPTY_MILESTONE_STEPS;
  const stepsLoading = stepsQuery.isLoading;
  const stepsError = stepsQuery.error;

  const rewardGroupsQuery = useQuery({
    queryKey: ['dilim', 'groups'],
    queryFn: listDilimRewardGroups,
  });
  const rewardGroups = rewardGroupsQuery.data ?? EMPTY_REWARD_GROUPS;
  const rewardGroupsLoading = rewardGroupsQuery.isLoading;
  const rewardGroupsError = rewardGroupsQuery.error;

  const eligibleRewardSizesQuery = useQuery({
    queryKey: ['dilim', 'eligible-reward-sizes'],
    queryFn: listEligibleDilimRewardSizes,
  });
  const eligibleRewardSizes = eligibleRewardSizesQuery.data ?? EMPTY_REWARD_SIZES;
  const eligibleSizesLoading = eligibleRewardSizesQuery.isLoading;
  const eligibleSizesError = eligibleRewardSizesQuery.error;

  const pointSizesQuery = useQuery({
    queryKey: ['dilim', 'point-sizes'],
    queryFn: listDilimPointProductSizes,
    enabled: activeTab === 'points',
  });
  const pointSizes = pointSizesQuery.data ?? EMPTY_POINT_SIZES;
  const pointSizesLoading = pointSizesQuery.isLoading;
  const pointSizesError = pointSizesQuery.error;

  const selectedGroupItemsQuery = useQuery({
    queryKey: ['dilim', 'group-items', selectedGroupId],
    queryFn: () => listDilimRewardGroupItems(selectedGroupId),
    enabled: Boolean(selectedGroupId) && rewardGroupsModalOpen && !isCreatingGroup,
  });
  const selectedGroupItems = selectedGroupItemsQuery.data ?? EMPTY_REWARD_GROUP_ITEMS;
  const selectedGroupItemsLoading = selectedGroupItemsQuery.isLoading;
  const selectedGroupItemsError = selectedGroupItemsQuery.error;

  useEffect(() => {
    setStepDrafts(buildStepDraftMap(steps));
  }, [steps]);

  useEffect(() => {
    if (rewardGroups.length === 0) {
      setSelectedGroupId(null);
      setGroupForm(EMPTY_GROUP_FORM);
      return;
    }

    const hasSelected = selectedGroupId
      ? rewardGroups.some((group) => group.id === selectedGroupId)
      : false;

    if (!hasSelected) {
      setSelectedGroupId(rewardGroups[0].id);
    }
  }, [rewardGroups, selectedGroupId]);

  useEffect(() => {
    if (isCreatingGroup) {
      return;
    }

    if (!selectedGroupId) {
      setGroupForm(EMPTY_GROUP_FORM);
      return;
    }

    const selectedGroup = rewardGroups.find((group) => group.id === selectedGroupId) ?? null;
    setGroupForm(buildGroupFormState(selectedGroup));
  }, [rewardGroups, selectedGroupId, isCreatingGroup]);

  useEffect(() => {
    setItemDrafts(
      selectedGroupItems.reduce<Record<string, RewardItemEditState>>((acc, item) => {
        acc[item.id] = {
          selectionWeight: String(item.selectionWeight),
          displayOrder: String(item.displayOrder),
          isActive: item.isActive,
        };
        return acc;
      }, {}),
    );
  }, [selectedGroupItems]);

  useEffect(() => {
    setPointDrafts(
      pointSizes.reduce<Record<string, string>>((acc, size) => {
        acc[size.id] = String(size.dilimPoints);
        return acc;
      }, {}),
    );
  }, [pointSizes]);

  const stepUsageByGroupId = useMemo(
    () =>
      steps.reduce<Record<string, number>>((acc, step) => {
        if (step.rewardGroupId) {
          acc[step.rewardGroupId] = (acc[step.rewardGroupId] ?? 0) + 1;
        }
        return acc;
      }, {}),
    [steps],
  );

  const activeGroup = useMemo(
    () => (isCreatingGroup ? null : rewardGroups.find((group) => group.id === selectedGroupId) ?? null),
    [rewardGroups, selectedGroupId, isCreatingGroup],
  );

  const selectedGroupItemSizeIds = useMemo(
    () => new Set(selectedGroupItems.map((item) => item.productSizeId)),
    [selectedGroupItems],
  );

  const filteredRewardSizes = useMemo(() => {
    const search = normalizeSearch(rewardSizeSearch);
    return eligibleRewardSizes
      .filter((size) => !selectedGroupItemSizeIds.has(size.id))
      .filter((size) => {
        if (!search) {
          return true;
        }

        const haystack = normalizeSearch(
          [size.productName, size.sizeName, size.productCategory, size.productType]
            .filter(Boolean)
            .join(' '),
        );
        return haystack.includes(search);
      })
      .slice(0, 120);
  }, [eligibleRewardSizes, rewardSizeSearch, selectedGroupItemSizeIds]);

  useEffect(() => {
    if (filteredRewardSizes.length === 0) {
      setSelectedRewardSizeId('');
      return;
    }

    if (!filteredRewardSizes.some((size) => size.id === selectedRewardSizeId)) {
      setSelectedRewardSizeId(filteredRewardSizes[0].id);
    }
  }, [filteredRewardSizes, selectedRewardSizeId]);

  const pointCategoryOptions = useMemo(
    () =>
      Array.from(new Set(pointSizes.map((size) => size.productCategory).filter(Boolean) as string[])).sort((a, b) =>
        a.localeCompare(b, 'tr'),
      ),
    [pointSizes],
  );

  const filteredPointSizes = useMemo(() => {
    const search = normalizeSearch(pointSearch);

    return pointSizes.filter((size) => {
      if (pointCategoryFilter !== 'all' && size.productCategory !== pointCategoryFilter) {
        return false;
      }

      const draftPoints = normalizePointDraft(pointDrafts[size.id] ?? String(size.dilimPoints));
      const isLikelyPizza = isLikelyPizzaPointRow(size);

      if (pointScope === 'earning' && draftPoints <= 0) {
        return false;
      }

      if (pointScope === 'zero' && draftPoints > 0) {
        return false;
      }

      if (pointScope === 'nonPizzaEarning' && (draftPoints <= 0 || isLikelyPizza)) {
        return false;
      }

      if (!search) {
        return true;
      }

      const haystack = normalizeSearch(
        [
          size.productName,
          size.productCategory,
          size.productType,
          size.sizeName,
          size.sizeCode,
        ]
          .filter(Boolean)
          .join(' '),
      );

      return haystack.includes(search);
    });
  }, [pointCategoryFilter, pointDrafts, pointScope, pointSearch, pointSizes]);

  const pointUpdates = useMemo(
    () =>
      pointSizes
        .map((size) => ({
          id: size.id,
          dilimPoints: normalizePointDraft(pointDrafts[size.id] ?? String(size.dilimPoints)),
          currentPoints: size.dilimPoints,
        }))
        .filter((update) => update.dilimPoints !== update.currentPoints)
        .map(({ id, dilimPoints }) => ({ id, dilimPoints })),
    [pointDrafts, pointSizes],
  );

  const pointSummary = useMemo(() => {
    const earningCount = pointSizes.filter(
      (size) => normalizePointDraft(pointDrafts[size.id] ?? String(size.dilimPoints)) > 0,
    ).length;
    const nonPizzaEarningCount = pointSizes.filter(
      (size) =>
        normalizePointDraft(pointDrafts[size.id] ?? String(size.dilimPoints)) > 0 &&
        !isLikelyPizzaPointRow(size),
    ).length;

    return {
      total: pointSizes.length,
      earningCount,
      nonPizzaEarningCount,
    };
  }, [pointDrafts, pointSizes]);

  const blockingLoadError = stepsError ?? rewardGroupsError ?? eligibleSizesError;
  const blockingLoadErrorMessage = useMemo(
    () => (blockingLoadError ? formatLoadErrorMessage(blockingLoadError) : null),
    [blockingLoadError],
  );
  const hasMissingBackendShape = useMemo(
    () => isMissingDilimBackendShapeError(blockingLoadError),
    [blockingLoadError],
  );
  const rewardGroupManagerLabel = rewardGroups.length === 0 ? '+ Ödül Grubu Ekle' : 'Ödül Gruplarını Yönet';

  const saveStepsMutation = useMutation({
    mutationFn: async () => {
      await Promise.all(
        STEP_NUMBERS.map((stepNumber) =>
          upsertDilimMilestoneStep({
            stepNumber,
            label: null,
            rewardGroupId: stepDrafts[stepNumber]?.rewardGroupId || null,
          }),
        ),
      );
    },
    onSuccess: () => {
      setStatusMessage('Milestone adımları kaydedildi.');
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'steps'] });
    },
    onError: (error) => {
      console.error('Failed to save Dilim milestone steps', error);
      setStatusMessage('Milestone adımları kaydedilemedi.');
    },
  });

  const upsertGroupMutation = useMutation({
    mutationFn: upsertDilimRewardGroup,
    onSuccess: (savedGroup) => {
      setStatusMessage('Ödül grubu kaydedildi.');
      setIsCreatingGroup(false);
      setSelectedGroupId(savedGroup.id);
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'groups'] });
    },
    onError: (error) => {
      console.error('Failed to save Dilim reward group', error);
      setStatusMessage('Ödül grubu kaydedilemedi.');
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: deleteDilimRewardGroup,
    onSuccess: (_, deletedGroupId) => {
      setStatusMessage('Ödül grubu silindi.');
      if (selectedGroupId === deletedGroupId) {
        setSelectedGroupId(null);
      }
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'groups'] });
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'steps'] });
    },
    onError: (error) => {
      console.error('Failed to delete Dilim reward group', error);
      setStatusMessage('Ödül grubu silinemedi.');
    },
  });

  const addRewardItemMutation = useMutation({
    mutationFn: upsertDilimRewardGroupItem,
    onSuccess: () => {
      setStatusMessage('Ödül havuzu güncellendi.');
      setNewItemWeight('1');
      setNewItemDisplayOrder('0');
      setNewItemActive(true);
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'group-items', selectedGroupId] });
    },
    onError: (error) => {
      console.error('Failed to add Dilim reward item', error);
      setStatusMessage('Ödül öğesi eklenemedi.');
    },
  });

  const updateRewardItemMutation = useMutation({
    mutationFn: upsertDilimRewardGroupItem,
    onSuccess: () => {
      setStatusMessage('Ödül öğesi kaydedildi.');
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'group-items', selectedGroupId] });
    },
    onError: (error) => {
      console.error('Failed to update Dilim reward item', error);
      setStatusMessage('Ödül öğesi kaydedilemedi.');
    },
  });

  const deleteRewardItemMutation = useMutation({
    mutationFn: deleteDilimRewardGroupItem,
    onSuccess: () => {
      setStatusMessage('Ödül öğesi kaldırıldı.');
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'group-items', selectedGroupId] });
    },
    onError: (error) => {
      console.error('Failed to delete Dilim reward item', error);
      setStatusMessage('Ödül öğesi kaldırılamadı.');
    },
  });

  const updatePointSizesMutation = useMutation({
    mutationFn: updateDilimPointProductSizes,
    onSuccess: (_, updates) => {
      setStatusMessage(`${updates.length} ürün boyutunun Dilim puanı kaydedildi.`);
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'point-sizes'] });
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'eligible-reward-sizes'] });
      void queryClient.invalidateQueries({ queryKey: ['dilim', 'group-items'] });
    },
    onError: (error) => {
      console.error('Failed to update Dilim point sizes', error);
      setStatusMessage('Dilim puanları kaydedilemedi.');
    },
  });

  const handleGroupSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusMessage(null);

    const name = groupForm.name.trim();
    const slug = groupForm.slug.trim();

    if (!name || !slug) {
      setStatusMessage('Ödül grubu için ad ve slug zorunludur.');
      return;
    }

    upsertGroupMutation.mutate({
      id: groupForm.id,
      name,
      slug,
      description: groupForm.description.trim() || null,
      isActive: groupForm.isActive,
    });
  };

  const handleAddRewardItem = () => {
    if (!selectedGroupId || !selectedRewardSizeId) {
      setStatusMessage('Önce bir ödül grubu ve ürün boyutu seçin.');
      return;
    }

    addRewardItemMutation.mutate({
      rewardGroupId: selectedGroupId,
      productSizeId: selectedRewardSizeId,
      selectionWeight: Number(newItemWeight) || 1,
      displayOrder: Number(newItemDisplayOrder) || 0,
      isActive: newItemActive,
    });
  };

  const handleSavePointSizes = () => {
    setStatusMessage(null);

    if (pointUpdates.length === 0) {
      setStatusMessage('Kaydedilecek Dilim puanı değişikliği yok.');
      return;
    }

    updatePointSizesMutation.mutate(pointUpdates);
  };

  const startCreatingGroup = useCallback(() => {
    setIsCreatingGroup(true);
    setGroupForm(EMPTY_GROUP_FORM);
  }, []);

  const openRewardGroupsModal = useCallback(() => {
    setRewardGroupsModalOpen(true);
    if (rewardGroups.length === 0) {
      startCreatingGroup();
    } else {
      setIsCreatingGroup(false);
    }
  }, [rewardGroups.length, startCreatingGroup]);

  const closeRewardGroupsModal = useCallback(() => {
    setRewardGroupsModalOpen(false);
    setIsCreatingGroup(false);
  }, []);

  const selectGroupForEditing = useCallback((group: DilimRewardGroupRecord) => {
    setIsCreatingGroup(false);
    setSelectedGroupId(group.id);
    setGroupForm(buildGroupFormState(group));
  }, []);

  if (hasMissingBackendShape) {
    return (
      <div className="page">
        <header className="page-header">
          <div>
            <h1>Dilim Milestones</h1>
            <p>
              10 adımlık Dilim hattını, sürpriz kutularını ve her kutudan çıkabilecek ürün boyutlarını yönetin.
            </p>
          </div>
        </header>

        <section className="page-section">
          <div className="card">
            <div className="empty-state" style={{ alignItems: 'flex-start' }}>
              <strong>Dilim milestone backend henüz bu projede yok</strong>
              <p>{blockingLoadErrorMessage}</p>
              <p className="helper-text">
                Bu sayfa, `dilim_milestone_steps`, `dilim_reward_groups`, `dilim_reward_group_items`
                tablolarını ve `product_sizes.dilim_points` kolonunu bekliyor.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Dilim Milestones</h1>
          <p>
            10 adımlık Dilim hattını, sürpriz kutularını ve her kutudan çıkabilecek ürün boyutlarını yönetin.
          </p>
        </div>
      </header>

      <section className="page-section">
        {statusMessage ? <p className="status-banner success">{statusMessage}</p> : null}

        <div className="tab-switch" role="tablist" aria-label="Dilim ayarları">
          <button
            type="button"
            className={`tab-button ${activeTab === 'milestones' ? 'active' : ''}`}
            onClick={() => setActiveTab('milestones')}
          >
            Milestone hattı
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'points' ? 'active' : ''}`}
            onClick={() => setActiveTab('points')}
          >
            Ürün puanları
          </button>
        </div>

        {activeTab === 'milestones' ? (
          <div className="card">
            <div className="card-header">
              <div>
                <p className="card-title">Milestone hattı</p>
                <p className="card-subtitle">
                  Her adımı düz checkpoint veya sürpriz kutusu olarak belirleyin.
                </p>
              </div>
              <div className="chip-row">
                <button type="button" className="button secondary" onClick={openRewardGroupsModal}>
                  {rewardGroupManagerLabel}
                </button>
                <button
                  type="button"
                  className="button primary"
                  onClick={() => saveStepsMutation.mutate()}
                  disabled={saveStepsMutation.isPending || stepsLoading}
                >
                  {saveStepsMutation.isPending ? 'Kaydediliyor…' : 'Adımları kaydet'}
                </button>
              </div>
            </div>

            {stepsLoading ? (
              <p className="helper-text">Adımlar yükleniyor…</p>
            ) : stepsError ? (
              <p className="error-text">Milestone adımları yüklenemedi.</p>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Adım</th>
                      <th>Sürpriz kutusu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {STEP_NUMBERS.map((stepNumber) => (
                      <tr key={stepNumber}>
                        <td>
                          <strong>{stepNumber}</strong>
                        </td>
                        <td>
                          <select
                            value={stepDrafts[stepNumber]?.rewardGroupId ?? ''}
                            onChange={(event) =>
                              setStepDrafts((prev) => ({
                                ...prev,
                                [stepNumber]: {
                                  ...(prev[stepNumber] ?? { rewardGroupId: '' }),
                                  rewardGroupId: event.target.value,
                                },
                              }))
                            }
                          >
                            <option value="">Kutusu yok</option>
                            {rewardGroups.map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="helper-text">
              Sürpriz kutularını ve içerik havuzlarını düzenlemek için <strong>{rewardGroupManagerLabel}</strong>{' '}
              butonunu kullanın.
            </p>
          </div>
        ) : (
          <div className="card">
            <div className="card-header">
              <div>
                <p className="card-title">Ürün puanları</p>
                <p className="card-subtitle">
                  Sipariş teslim edildiğinde hangi ürün boyutunun kaç Dilim ilerlemesi kazandıracağını yönetin.
                </p>
              </div>
              <div className="chip-row">
                <span className="chip neutral">{pointSummary.total} boyut</span>
                <span className="chip success">{pointSummary.earningCount} puanlı</span>
                {pointSummary.nonPizzaEarningCount > 0 ? (
                  <span className="chip warning">{pointSummary.nonPizzaEarningCount} pizza dışı puanlı</span>
                ) : null}
                <button
                  type="button"
                  className="button primary"
                  onClick={handleSavePointSizes}
                  disabled={updatePointSizesMutation.isPending || pointSizesLoading || pointUpdates.length === 0}
                >
                  {updatePointSizesMutation.isPending
                    ? 'Kaydediliyor…'
                    : pointUpdates.length > 0
                      ? `${pointUpdates.length} değişikliği kaydet`
                      : 'Puanları kaydet'}
                </button>
              </div>
            </div>

            <div className="form-grid three-columns">
              <label className="form-field">
                <span>Ürün ara</span>
                <input
                  type="text"
                  value={pointSearch}
                  onChange={(event) => setPointSearch(event.target.value)}
                  placeholder="Ürün, kategori veya boyut"
                />
              </label>
              <label className="form-field">
                <span>Kategori</span>
                <select
                  value={pointCategoryFilter}
                  onChange={(event) => setPointCategoryFilter(event.target.value)}
                >
                  <option value="all">Tüm kategoriler</option>
                  {pointCategoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
              <div className="form-field">
                <span>Göster</span>
                <div className="segmented-control" role="group" aria-label="Puan filtresi">
                  <button
                    type="button"
                    className={pointScope === 'all' ? 'active' : ''}
                    onClick={() => setPointScope('all')}
                  >
                    Tümü
                  </button>
                  <button
                    type="button"
                    className={pointScope === 'earning' ? 'active' : ''}
                    onClick={() => setPointScope('earning')}
                  >
                    Puanlı
                  </button>
                  <button
                    type="button"
                    className={pointScope === 'zero' ? 'active' : ''}
                    onClick={() => setPointScope('zero')}
                  >
                    0 puan
                  </button>
                  <button
                    type="button"
                    className={pointScope === 'nonPizzaEarning' ? 'active' : ''}
                    onClick={() => setPointScope('nonPizzaEarning')}
                  >
                    Pizza dışı puanlı
                  </button>
                </div>
              </div>
            </div>

            {pointSizesLoading ? (
              <p className="helper-text">Ürün puanları yükleniyor…</p>
            ) : pointSizesError ? (
              <p className="error-text">{formatLoadErrorMessage(pointSizesError)}</p>
            ) : filteredPointSizes.length === 0 ? (
              <div className="empty-state">
                <strong>Ürün boyutu bulunamadı</strong>
                <p>Arama veya filtreleri değiştirerek tekrar deneyin.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th>Kategori</th>
                      <th>Boyut</th>
                      <th>Dilim puanı</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPointSizes.map((size) => {
                      const draftValue = pointDrafts[size.id] ?? String(size.dilimPoints);
                      const draftPoints = normalizePointDraft(draftValue);
                      const isChanged = draftPoints !== size.dilimPoints;

                      return (
                        <tr key={size.id} className={isChanged ? 'active' : ''}>
                          <td>
                            <strong>{size.productName}</strong>
                            <p className="helper-text">{size.productType ?? 'type yok'}</p>
                          </td>
                          <td>
                            <span className={`chip ${isLikelyPizzaPointRow(size) ? 'neutral' : 'warning'}`}>
                              {size.productCategory ?? 'kategori yok'}
                            </span>
                          </td>
                          <td>
                            <strong>{size.sizeName}</strong>
                            <p className="helper-text">
                              {size.sizeCode ?? 'kod yok'}
                              {size.isDefault ? ' • varsayılan' : ''}
                            </p>
                          </td>
                          <td>
                            <input
                              className="inline-input"
                              type="number"
                              min="0"
                              step="1"
                              value={draftValue}
                              onChange={(event) =>
                                setPointDrafts((prev) => ({
                                  ...prev,
                                  [size.id]: event.target.value,
                                }))
                              }
                              onBlur={() =>
                                setPointDrafts((prev) => ({
                                  ...prev,
                                  [size.id]: String(normalizePointDraft(prev[size.id] ?? '0')),
                                }))
                              }
                            />
                          </td>
                          <td>
                            <div className="chip-row">
                              <span className={`chip ${draftPoints > 0 ? 'success' : 'neutral'}`}>
                                {draftPoints > 0 ? 'Puan verir' : 'Puan vermez'}
                              </span>
                              {!size.productIsAvailable || !size.isAvailable ? (
                                <span className="chip warning">Pasif</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      <Modal
        open={rewardGroupsModalOpen}
        onClose={closeRewardGroupsModal}
        title={isCreatingGroup ? 'Yeni ödül grubu' : 'Ödül gruplarını yönet'}
        extraWide
      >
        <div className="page-stack" style={{ gap: '1.5rem' }}>
          <div className="card">
            <div className="card-header">
              <div>
                <p className="card-title">Ödül grupları</p>
                <p className="card-subtitle">
                  İçerik havuzlarını yönetip milestone adımlarına bağlayın.
                </p>
              </div>
              <button
                type="button"
                className="button secondary"
                onClick={startCreatingGroup}
              >
                Yeni grup
              </button>
            </div>

            {rewardGroupsLoading ? (
              <p className="helper-text">Ödül grupları yükleniyor…</p>
            ) : rewardGroupsError ? (
              <p className="error-text">Ödül grupları yüklenemedi.</p>
            ) : rewardGroups.length === 0 ? (
              <div className="empty-state">
                <strong>Henüz ödül grubu yok</strong>
                <p>İçecek, atıştırmalık veya pizza gibi bir sürpriz havuzu oluşturun.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Grup</th>
                      <th>Durum</th>
                      <th>Adım kullanımı</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rewardGroups.map((group) => (
                      <tr key={group.id} className={!isCreatingGroup && selectedGroupId === group.id ? 'active' : ''}>
                        <td>
                          <strong>{group.name}</strong>
                          <p className="helper-text">{group.slug}</p>
                        </td>
                        <td>
                          <span className={`chip ${group.isActive ? 'success' : 'warning'}`}>
                            {group.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td>{stepUsageByGroupId[group.id] ?? 0} adım</td>
                        <td>
                          <div className="chip-row">
                            <button
                              type="button"
                              className="button secondary"
                              onClick={() => selectGroupForEditing(group)}
                            >
                              Düzenle
                            </button>
                            <button
                              type="button"
                              className="button danger"
                              onClick={() => {
                                if (window.confirm(`${group.name} grubunu silmek istediğinize emin misiniz?`)) {
                                  deleteGroupMutation.mutate(group.id);
                                }
                              }}
                            >
                              Sil
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <form className="form-grid" onSubmit={handleGroupSubmit}>
              <div className="form-grid two-columns">
                <label className="form-field">
                  <span>Grup adı</span>
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(event) =>
                      setGroupForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                  />
                </label>
                <label className="form-field">
                  <span>Slug</span>
                  <input
                    type="text"
                    value={groupForm.slug}
                    onChange={(event) =>
                      setGroupForm((prev) => ({ ...prev, slug: event.target.value }))
                    }
                  />
                </label>
              </div>
              <label className="form-field">
                <span>Açıklama</span>
                <textarea
                  value={groupForm.description}
                  onChange={(event) =>
                    setGroupForm((prev) => ({ ...prev, description: event.target.value }))
                  }
                />
              </label>
              <label className="form-field checkbox">
                <input
                  type="checkbox"
                  checked={groupForm.isActive}
                  onChange={(event) =>
                    setGroupForm((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
                <span>Grup aktif</span>
              </label>
              <div className="form-actions">
                <button
                  type="submit"
                  className="button primary"
                  disabled={upsertGroupMutation.isPending}
                >
                  {upsertGroupMutation.isPending ? 'Kaydediliyor…' : 'Grubu kaydet'}
                </button>
              </div>
            </form>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <p className="card-title">
                  {activeGroup ? `${activeGroup.name} içerikleri` : 'Ödül içeriği seçin'}
                </p>
                <p className="card-subtitle">
                  Bu kutudan çıkabilecek ürün ve boyutları yönetin.
                </p>
              </div>
            </div>

            {isCreatingGroup ? (
              <div className="empty-state">
                <strong>Yeni ödül grubunu önce kaydedin</strong>
                <p>Grubu kaydettikten sonra bu sürpriz kutusuna ürün boyutları ekleyebilirsiniz.</p>
              </div>
            ) : !activeGroup ? (
              <div className="empty-state">
                <strong>Önce bir ödül grubu seçin</strong>
                <p>Yukarıdaki listeden bir grup seçince içerik havuzu burada açılır.</p>
              </div>
            ) : (
              <>
                <div className="form-grid">
                  <div className="form-grid two-columns">
                    <label className="form-field">
                      <span>Ürün boyutu ara</span>
                      <input
                        type="text"
                        value={rewardSizeSearch}
                        onChange={(event) => setRewardSizeSearch(event.target.value)}
                        placeholder="Pizza, içecek, boyut veya kategori"
                      />
                    </label>
                    <label className="form-field">
                      <span>Ürün boyutu</span>
                      <select
                        value={selectedRewardSizeId}
                        onChange={(event) => setSelectedRewardSizeId(event.target.value)}
                        disabled={filteredRewardSizes.length === 0}
                      >
                        {filteredRewardSizes.length === 0 ? (
                          <option value="">Uygun ürün bulunamadı</option>
                        ) : (
                          filteredRewardSizes.map((size) => (
                            <option key={size.id} value={size.id}>
                              {size.productName ?? 'Ürün'} • {size.sizeName}
                              {size.productCategory ? ` • ${size.productCategory}` : ''}
                            </option>
                          ))
                        )}
                      </select>
                    </label>
                  </div>
                  <div className="form-grid three-columns">
                    <label className="form-field">
                      <span>Seçilme ağırlığı</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={newItemWeight}
                        onChange={(event) => setNewItemWeight(event.target.value)}
                      />
                    </label>
                    <label className="form-field">
                      <span>Sıralama</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={newItemDisplayOrder}
                        onChange={(event) => setNewItemDisplayOrder(event.target.value)}
                      />
                    </label>
                    <label className="form-field checkbox">
                      <input
                        type="checkbox"
                        checked={newItemActive}
                        onChange={(event) => setNewItemActive(event.target.checked)}
                      />
                      <span>Eklenen öğe aktif</span>
                    </label>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="button primary"
                      onClick={handleAddRewardItem}
                      disabled={
                        addRewardItemMutation.isPending ||
                        !selectedGroupId ||
                        !selectedRewardSizeId ||
                        filteredRewardSizes.length === 0
                      }
                    >
                      {addRewardItemMutation.isPending ? 'Ekleniyor…' : 'Öğe ekle'}
                    </button>
                  </div>
                </div>

                {eligibleSizesLoading ? (
                  <p className="helper-text">Uygun ürün boyutları yükleniyor…</p>
                ) : eligibleSizesError ? (
                  <p className="error-text">Uygun ürün boyutları yüklenemedi.</p>
                ) : selectedGroupItemsLoading ? (
                  <p className="helper-text">Ödül içeriği yükleniyor…</p>
                ) : selectedGroupItemsError ? (
                  <p className="error-text">Ödül içeriği yüklenemedi.</p>
                ) : selectedGroupItems.length === 0 ? (
                  <div className="empty-state">
                    <strong>Henüz ürün eklenmedi</strong>
                    <p>Bu sürpriz kutusundan çıkabilecek ürün boyutlarını yukarıdan ekleyin.</p>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Ürün</th>
                          <th>Dilim puanı</th>
                          <th>Ağırlık</th>
                          <th>Sıra</th>
                          <th>Durum</th>
                          <th>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedGroupItems.map((item) => {
                          const draft = itemDrafts[item.id] ?? {
                            selectionWeight: String(item.selectionWeight),
                            displayOrder: String(item.displayOrder),
                            isActive: item.isActive,
                          };

                          return (
                            <tr key={item.id}>
                              <td>
                                <strong>{item.productName ?? 'Ürün'}</strong>
                                <p className="helper-text">
                                  {item.sizeName ?? 'Boyut yok'}
                                  {item.productCategory ? ` • ${item.productCategory}` : ''}
                                </p>
                              </td>
                              <td>{item.dilimPoints}</td>
                              <td>
                                <input
                                  className="inline-input"
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={draft.selectionWeight}
                                  onChange={(event) =>
                                    setItemDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: {
                                        ...draft,
                                        selectionWeight: event.target.value,
                                      },
                                    }))
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="inline-input"
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={draft.displayOrder}
                                  onChange={(event) =>
                                    setItemDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: {
                                        ...draft,
                                        displayOrder: event.target.value,
                                      },
                                    }))
                                  }
                                />
                              </td>
                              <td>
                                <label className="checkbox-item">
                                  <input
                                    type="checkbox"
                                    checked={draft.isActive}
                                    onChange={(event) =>
                                      setItemDrafts((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...draft,
                                          isActive: event.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                  <span>{draft.isActive ? 'Aktif' : 'Pasif'}</span>
                                </label>
                              </td>
                              <td>
                                <div className="chip-row">
                                  <button
                                    type="button"
                                    className="button secondary"
                                    onClick={() =>
                                      updateRewardItemMutation.mutate({
                                        id: item.id,
                                        rewardGroupId: item.rewardGroupId,
                                        productSizeId: item.productSizeId,
                                        selectionWeight: Number(draft.selectionWeight) || 1,
                                        displayOrder: Number(draft.displayOrder) || 0,
                                        isActive: draft.isActive,
                                      })
                                    }
                                  >
                                    Kaydet
                                  </button>
                                  <button
                                    type="button"
                                    className="button danger"
                                    onClick={() => {
                                      if (window.confirm('Bu ödül öğesini kaldırmak istediğinize emin misiniz?')) {
                                        deleteRewardItemMutation.mutate(item.id);
                                      }
                                    }}
                                  >
                                    Sil
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeRewardGroupsModal}>
              Kapat
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
